import { authLogger } from '@/shared/lib/logger';
import { AuthTokenService } from '@/shared/lib/auth-token-service';
import { cacheInvalidation, cacheKeys } from '@/shared/lib/cache-invalidation';
import { queryClient } from '@/shared/lib/query-client';
import type { User } from '@/types/user';

// ===== Auth data =====
//
// `User` itself is NOT a field on the store — it lives in the TanStack Query
// cache (`cacheKeys.user`) per STATE_POLICY. The store keeps only what isn't
// server-authored: the bearer token + auth-lifecycle booleans + per-flow
// mutation flags + error strings.
export interface AuthData {
  // Identity
  token: string | null;
  isInitialized: boolean;
  isAuthenticated: boolean;
  pendingEmailVerification: boolean;

  // Mutation flow flags
  isLoggingIn: boolean;
  isRegistering: boolean;
  isLoggingOut: boolean;
  isRefreshingUser: boolean;
  isSendingPasswordReset: boolean;
  isResettingPassword: boolean;

  // Per-flow error strings (rendered inline in the auth forms)
  loginError: string | null;
  registerError: string | null;
  refreshUserError: string | null;
  forgotPasswordError: string | null;
  resetPasswordError: string | null;
}

export const initialAuthData: AuthData = {
  token: null,
  isInitialized: false,
  isAuthenticated: false,
  pendingEmailVerification: false,

  isLoggingIn: false,
  isRegistering: false,
  isLoggingOut: false,
  isRefreshingUser: false,
  isSendingPasswordReset: false,
  isResettingPassword: false,

  loginError: null,
  registerError: null,
  refreshUserError: null,
  forgotPasswordError: null,
  resetPasswordError: null,
};

// ===== User cache helpers =====

/**
 * Set the canonical User in the Query cache (STATE_POLICY: server data →
 * Query). `null` evicts. Auth flows / WS handler / initializer all funnel
 * through here so there's exactly one write channel.
 */
export function setCachedUser(user: User | null): void {
  if (user) {
    queryClient.setQueryData<User>(cacheKeys.user, user);
  } else {
    queryClient.removeQueries({ queryKey: cacheKeys.user });
  }
}

export function getCachedUser(): User | null {
  return queryClient.getQueryData<User>(cacheKeys.user) ?? null;
}

/**
 * Greenfield-prod detection: if the newly-authenticated account differs from
 * the one in encrypted localStorage, evict the persisted TanStack Query cache
 * (IndexedDB) before new auth state is set. Otherwise queries like
 * `['projects','detail',id]` would rehydrate with the prior user's rows — same
 * symptom as Android's Room fallback-to-cache bug. Email is not a valid
 * identity key (a re-registered user gets a fresh user_id but may reuse the
 * same email), so we must compare `user.id`.
 */
export async function wipePersistedCachesIfIdentityChanged(
  newUserId: string
): Promise<void> {
  const previousUser = await AuthTokenService.getUser();
  if (previousUser && previousUser.id !== newUserId) {
    authLogger.warn(
      `Identity changed (${previousUser.id} -> ${newUserId}), wiping persisted caches`
    );
    await cacheInvalidation.afterLogout(previousUser.id);
  }
}
